// Frais, cagnotte du titulaire et charges fixes — logique PURE et testée.
//
// Ce module porte des règles qui ont des conséquences comptables et fiscales.
// Chacune est ici, testée, plutôt que dispersée dans le rendu : c'est le seul
// moyen de vérifier qu'un solde reste juste après un reclassement rétroactif.

// ── Montants : HT, TVA, TTC ──────────────────────────────────────────────────

/**
 * Complète le triplet HT / TVA / TTC à partir de ce qui est connu.
 *
 * Le piège central de ce module : **le titulaire avance du TTC, l'entreprise
 * déduit du HT et récupère la TVA**. Confondre les deux fait dériver la cagnotte
 * exactement du montant de la TVA — et personne ne s'en aperçoit avant le bilan.
 * La cagnotte se raisonne donc TOUJOURS en TTC ; le HT ne sert qu'à la
 * comptabilité de l'officine.
 */
export function completerMontants({ ttc, tva, ht, tauxTva } = {}) {
  const n = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
  let T = n(ttc), V = n(tva), H = n(ht);
  const taux = n(tauxTva);

  if (T != null && H != null && V == null) V = arrondi(T - H);
  else if (T != null && V != null && H == null) H = arrondi(T - V);
  else if (H != null && V != null && T == null) T = arrondi(H + V);
  else if (T != null && taux != null && H == null && V == null) {
    H = arrondi(T / (1 + taux / 100));
    V = arrondi(T - H);
  } else if (H != null && taux != null && T == null && V == null) {
    V = arrondi(H * (taux / 100));
    T = arrondi(H + V);
  }
  return { ttc: T, tva: V, ht: H };
}

const arrondi = (v) => Math.round(v * 100) / 100;

/**
 * Le triplet se tient-il ? Tolérance d'un centime pour les arrondis de TVA.
 *
 * La comparaison se fait en CENTIMES ENTIERS, jamais en euros flottants.
 * `120 - (99.99 + 20)` vaut 0.010000000000005116 en virgule flottante, donc
 * strictement supérieur à 0,01 : une note parfaitement valide était rejetée
 * comme incohérente. C'est un test qui l'a attrapé, et c'est le genre de défaut
 * qui ne se voit qu'à la ligne près, une fois sur cent.
 */
export function montantsCoherents({ ttc, tva, ht }) {
  if (ttc == null || tva == null || ht == null) return true; // rien à contredire
  const centimes = (v) => Math.round(Number(v) * 100);
  return Math.abs(centimes(ttc) - (centimes(ht) + centimes(tva))) <= 1;
}

// ── Notes de frais ───────────────────────────────────────────────────────────

/**
 * Vérifie une note avant enregistrement.
 * Retourne la liste des problèmes ; vide = bon pour l'enregistrement.
 */
export function validerNote(note) {
  const pbs = [];
  if (!note || typeof note !== "object") return ["note vide"];

  if (!note.libelle || !String(note.libelle).trim()) pbs.push("Le libellé est obligatoire.");
  if (!note.date_depense) pbs.push("La date de la dépense est obligatoire.");
  else if (note.date_depense > aujourdhuiISO()) pbs.push("La date de dépense est dans le futur.");

  const m = Number(note.montant_ttc);
  if (!Number.isFinite(m) || m === 0) pbs.push("Le montant TTC est obligatoire.");
  else if (m < 0) {
    // Refusé, jamais redressé : une note négative validée créerait une avance
    // POSITIVE en cagnotte, donc de l'argent, et le mouvement est immuable en
    // base. Corriger se fait par l'annulation de la note ou un ajustement
    // motivé — qui exigent tous deux un motif écrit, là où une note négative
    // n'en exigerait aucun. Le refus est posé aussi côté serveur : le champ de
    // saisie porte min="0", mais un min d'UI ne protège aucune donnée.
    pbs.push("Le montant TTC doit être positif : une note de frais constate une dépense, elle n'en rend pas.");
  }

  if (!["pro", "perso"].includes(note.nature)) {
    pbs.push("Précisez s'il s'agit d'une dépense de l'officine ou d'une avance personnelle.");
  }
  if (!montantsCoherents(note)) {
    pbs.push("HT + TVA ne correspond pas au TTC.");
  }
  // Le justificatif n'est pas exigé au brouillon, mais il l'est à la validation :
  // sans pièce, la dépense n'est pas déductible, et la note ne vaut rien.
  if (note.statut === "validee" && !note.justificatif_path) {
    pbs.push("Une note ne peut être validée sans son justificatif.");
  }
  return pbs;
}

function aujourdhuiISO(d = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Mouvement de cagnotte engendré par une note.
 * Seules les avances PERSONNELLES alimentent la cagnotte : une dépense de
 * l'officine payée par l'officine ne crée aucune créance.
 */
export function mouvementDepuisNote(note) {
  if (!note || note.nature !== "perso" || note.statut !== "validee") return null;
  return {
    sens: "avance",
    // Signe conservé, pas de Math.abs : c'est le CHECK cagnotte_signe_coherent
    // qui doit refuser une avance négative. Le redresser ici ferait passer un
    // montant négatif devant la garde, sous la forme d'une avance positive.
    montant_ttc: Number(note.montant_ttc),
    date_mouvement: note.date_depense,
    note_frais_id: note.id ?? null,
  };
}

// ── Cagnotte ─────────────────────────────────────────────────────────────────

/**
 * Solde, à partir des mouvements. Jamais stocké : recalculé.
 * Positif = l'officine doit de l'argent au titulaire.
 */
export function soldeCagnotte(mouvements) {
  const l = Array.isArray(mouvements) ? mouvements : [];
  const somme = (f) => arrondi(l.filter(f).reduce((s, m) => s + Number(m.montant_ttc || 0), 0));
  return {
    solde: somme(() => true),
    avances: somme((m) => m.sens === "avance"),
    rembourse: -somme((m) => m.sens === "remboursement"),
    ajustements: somme((m) => m.sens === "ajustement"),
    n: l.length,
  };
}

/** Vérifie un remboursement avant enregistrement. */
export function validerRemboursement(mvt, soldeActuel) {
  const pbs = [];
  const montant = Math.abs(Number(mvt?.montant_ttc));
  if (!Number.isFinite(montant) || montant === 0) pbs.push("Le montant est obligatoire.");
  if (!mvt?.mode_recuperation) {
    // Contrainte aussi posée en base : sans le mode, on ne peut pas justifier
    // le mouvement six mois plus tard.
    pbs.push("Précisez comment vous avez récupéré l'argent (virement, espèces, chèque…).");
  }
  if (Number.isFinite(montant) && montant > soldeActuel + 0.01) {
    // Ce n'est pas une erreur bloquante : un titulaire PEUT se verser plus que
    // sa créance. Mais ça change de nature comptable, donc ça se signale.
    pbs.push(
      `Attention : vous retirez plus que ce que l'officine vous doit (solde ${arrondi(soldeActuel)} €). ` +
        `Au-delà, ce n'est plus un remboursement de frais.`,
    );
  }
  return pbs;
}

// ── Charges fixes ────────────────────────────────────────────────────────────

const MOIS_PAR_PERIODE = {
  mensuelle: 1,
  bimestrielle: 2,
  trimestrielle: 3,
  semestrielle: 6,
  annuelle: 12,
};

/**
 * Projette les occurrences d'une charge sur une fenêtre.
 *
 * On ne stocke PAS une ligne par occurrence : elle dériverait au premier
 * changement de montant. On projette à la lecture, à partir de la périodicité.
 *
 * Le jour d'échéance est ramené au dernier jour du mois quand il n'existe pas
 * (un prélèvement « le 31 » tombe le 28 ou 29 en février — sans ça, l'occurrence
 * disparaîtrait de la projection un mois sur douze).
 */
export function projeterCharge(charge, debutISO, finISO) {
  if (!charge || !charge.debut_le) return [];
  const pas = MOIS_PAR_PERIODE[charge.periodicite];
  if (!pas) return [];

  const debut = new Date(debutISO + "T00:00:00");
  const fin = new Date(finISO + "T00:00:00");
  const chargeDebut = new Date(charge.debut_le + "T00:00:00");
  const chargeFin = charge.fin_le ? new Date(charge.fin_le + "T00:00:00") : null;
  if (Number.isNaN(debut.getTime()) || Number.isNaN(fin.getTime())) return [];

  const jour = Number(charge.jour_echeance) || chargeDebut.getDate();
  const out = [];

  // On part de la première occurrence et on avance : le calage sur la date de
  // début de la charge est ce qui rend une charge trimestrielle prévisible.
  let curseur = new Date(chargeDebut.getFullYear(), chargeDebut.getMonth(), 1);
  const garde = 2000; // borne de sûreté, jamais atteinte en usage normal
  for (let i = 0; i < garde; i++) {
    const dernierJour = new Date(curseur.getFullYear(), curseur.getMonth() + 1, 0).getDate();
    const d = new Date(curseur.getFullYear(), curseur.getMonth(), Math.min(jour, dernierJour));

    if (d > fin) break;
    const dansLaVie = d >= chargeDebut && (!chargeFin || d <= chargeFin);
    if (d >= debut && dansLaVie) {
      out.push({
        date: isoDe(d),
        libelle: charge.libelle,
        categorie: charge.categorie,
        montant_ttc: Number(charge.montant_ttc),
        charge_id: charge.id ?? null,
      });
    }
    curseur = new Date(curseur.getFullYear(), curseur.getMonth() + pas, 1);
  }
  return out;
}

function isoDe(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Projette un ensemble de charges et totalise par mois. */
export function projeterToutes(charges, debutISO, finISO) {
  const actives = (Array.isArray(charges) ? charges : []).filter((c) => c.actif !== false);
  const occurrences = actives.flatMap((c) => projeterCharge(c, debutISO, finISO));
  occurrences.sort((a, b) => a.date.localeCompare(b.date));

  const parMois = new Map();
  for (const o of occurrences) {
    const mois = o.date.slice(0, 7);
    parMois.set(mois, arrondi((parMois.get(mois) ?? 0) + o.montant_ttc));
  }
  return {
    occurrences,
    parMois: [...parMois.entries()].map(([mois, total]) => ({ mois, total })),
    total: arrondi(occurrences.reduce((s, o) => s + o.montant_ttc, 0)),
  };
}

/** Coût mensuel moyen d'une charge, toutes périodicités ramenées au mois. */
export function coutMensuelEquivalent(charge) {
  const pas = MOIS_PAR_PERIODE[charge?.periodicite];
  if (!pas || !Number.isFinite(Number(charge.montant_ttc))) return null;
  return arrondi(Number(charge.montant_ttc) / pas);
}
